import React, { useEffect, useState, createContext } from 'react';
import { cn } from '../../lib/utils';
import { useMotionValue, motion, useMotionTemplate, AnimatePresence } from 'framer-motion';
// Create a context to track dialog open state
export const DialogContext = createContext<{
  isDialogOpen: boolean;
}>({
  isDialogOpen: false
});
export const HeroHighlight = ({
  children,
  className,
  containerClassName
}: {
  children: React.ReactNode;
  className?: string;
  containerClassName?: string;
}) => {
  let mouseX = useMotionValue(0);
  let mouseY = useMotionValue(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);
  useEffect(() => {
    if (!isMobile) return;
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const viewportHeight = window.innerHeight;
      const documentHeight = document.body.scrollHeight;
      const videoSectionOffset = viewportHeight * 0.15; // Reduced offset for mobile
      const adjustedScrollY = Math.max(0, scrollY - videoSectionOffset);
      const adjustedDocumentHeight = documentHeight - videoSectionOffset;
      const scrollPercentY = Math.min(adjustedScrollY / (adjustedDocumentHeight - viewportHeight), 1);
      mouseX.set(window.innerWidth / 2);
      mouseY.set(videoSectionOffset + scrollPercentY * (documentHeight - viewportHeight));
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isMobile, mouseX, mouseY]);
  // Check for dialog open state
  useEffect(() => {
    const checkForDialog = () => {
      // Check if any dialog is currently open by looking for elements with aria-modal="true"
      const openDialogs = document.querySelectorAll('[aria-modal="true"]');
      setIsDialogOpen(openDialogs.length > 0);
    };
    // Check initially and set up a MutationObserver to detect DOM changes
    checkForDialog();
    const observer = new MutationObserver(checkForDialog);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-modal']
    });
    return () => observer.disconnect();
  }, []);
  function handleMouseMove({
    currentTarget,
    clientX,
    clientY
  }: React.MouseEvent<HTMLDivElement>) {
    // Don't track mouse movement if dialog is open or on mobile
    if (isMobile || isDialogOpen) return;
    if (!currentTarget) return;
    let {
      left,
      top
    } = currentTarget.getBoundingClientRect();
    mouseX.set(clientX - left);
    mouseY.set(clientY - top);
  }
  const dotPattern = (color: string) => ({
    backgroundImage: `radial-gradient(circle, ${color} 1px, transparent 1px)`,
    backgroundSize: '16px 16px'
  });
  return <DialogContext.Provider value={{
    isDialogOpen
  }} data-id="element-86">
      <AnimatePresence data-id="element-87">
        <motion.div className={cn('relative min-h-screen flex items-start bg-[#050b2e] justify-center w-full group', containerClassName)} onMouseMove={handleMouseMove} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.3
      }} data-id="element-88">
          <motion.div className="absolute inset-0 pointer-events-none opacity-20" style={dotPattern('rgb(255 255 255)')} initial={{
          opacity: 0
        }} animate={{
          opacity: 0.2
        }} transition={{
          duration: 0.5
        }} data-id="element-89" />
          <motion.div className={cn('pointer-events-none absolute inset-0', isMobile || isDialogOpen ? 'hidden' : 'opacity-0 transition duration-300 group-hover:opacity-100')} style={{
          backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.5) 1px, transparent 1px)`,
          backgroundSize: '16px 16px',
          WebkitMaskImage: useMotionTemplate`
                radial-gradient(
                  250px circle at ${mouseX}px ${mouseY}px,
                  white,
                  transparent
                )
              `,
          maskImage: useMotionTemplate`
                radial-gradient(
                  250px circle at ${mouseX}px ${mouseY}px,
                  white,
                  transparent
                )
              `
        }} data-id="element-90" />
          <div className={cn('relative z-20 w-full', className)} data-id="element-91">
            {children}
          </div>
        </motion.div>
      </AnimatePresence>
    </DialogContext.Provider>;
};
export const Highlight = ({
  children,
  className
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return <motion.span initial={{
    backgroundSize: '0% 100%'
  }} whileInView={{
    backgroundSize: '100% 100%'
  }} viewport={{
    once: true,
    margin: '-10% 0px'
  }} transition={{
    duration: 1.5,
    ease: 'linear',
    delay: 0.2
  }} style={{
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'left center',
    display: 'inline'
  }} className={cn('relative inline-block pb-1 px-1 rounded-lg bg-gradient-to-r from-blue-400/80 to-indigo-600/80', className)} data-id="element-92">
      {children}
    </motion.span>;
};